import React, { Fragment, PureComponent } from 'react'

import Immutable from 'immutable'

import throttle from 'lodash/throttle'
import debounce from 'lodash/debounce'

import {
  CellMeasurer,
  CellMeasurerCache,
  createMasonryCellPositioner,
  Masonry,
  AutoSizer,
  WindowScroller,
} from 'react-virtualized'
import { compose, withRouter, withTranslation } from '@hocs'

import Loading from '@components/Loading'
import cx from 'classnames'
import styles from './index.scss'

const GUTTER_SIZE = 13
const DEFAULT_COLUMN_COUNT = 2
const DEFAULT_COLUMN_WIDTH = 230

const getWindow = () => (typeof window !== 'undefined' ? window : undefined)

class MasonryVirtualized extends PureComponent {
  /* how to use fixed */
  /*
    fixed={[0, 1, 3, 5, 7]}   // fixed start from cell's index.
  */

  /* how to use columns */
  /*
    columns={[null, 0]}       // 1欄: pass 2欄: 0px 以上
    columns={[0, null, 900]}  // 1欄: 0px以上 2欄: pass 3欄: 900px 以上
    ...
    columns: [null, 0, 720, null, 1140]
    // 1欄: pass 2欄: 0px 以上 3欄: 720px 以上 4欄: pass 5欄: 1140px 以上
  */

  static defaultProps = {
    router: {},
    cells: Immutable.fromJS([]),
    fixed: [],
    columns: [null, 0, 720],
    hasMore: true,
    isLoading: false,
    initInfiniteScroll: true,
    handleRenderCell: () => null,
    handleInfiniteScroll: () => false,
    scrollElement: getWindow(),
    t: () => false,
  }

  constructor(props) {
    super(props)

    this._width = 0
    this._columnCount = DEFAULT_COLUMN_COUNT
    this._columnWidth = DEFAULT_COLUMN_WIDTH
    this._spacer = GUTTER_SIZE
    this.fixedHeight = false
    this.fixedWidth = true

    this._cache = new CellMeasurerCache({
      defaultHeight: this._columnWidth,
      defaultWidth: this._columnWidth,
      fixedHeight: this.fixedHeight,
      fixedWidth: this.fixedWidth,
    })

    this.state = {
      cellsSize: props.cells.size || 0,
    }

    // global methods.
    this._masonryFocusUpdate = debounce(this._masonryFocusUpdate, 16)
    this._masonryFocusRecompute = debounce(this._masonryFocusRecompute, 16)

    // public method on window.
    if (typeof window !== 'undefined') window.masonryFocusUpdate = this._masonryFocusUpdate
    if (typeof window !== 'undefined') window.masonryFocusRecompute = this._masonryFocusRecompute

    this._onResize = debounce(this._onResize, 16)
    this._handleInfiniteScroll = throttle(props.handleInfiniteScroll, 64)
  }

  componentDidMount() {
    this.props.initInfiniteScroll && this._handleCellsRendered()
  }

  componentDidUpdate(prevProps) {
    if (this.props.cells.size > prevProps.cells.size) {
      this.setState({ cellsSize: this.props.cells.size })
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      delete window._masonry
    }
    this._masonrylayout = null
    this._masonry = null
  }

  calculateDefaultColumnCount() {
    let columnCount = DEFAULT_COLUMN_COUNT

    columnCount = this._width >= 720 ? 3 : columnCount

    const bodyWidth = columnCount > 2 ? this._width : this._width - GUTTER_SIZE + 1
    const baseWidth = bodyWidth - (columnCount > 2 ? columnCount - 1 : columnCount) * this._spacer
    const columnWidth = Math.floor(baseWidth / columnCount)

    return { bodyWidth, columnCount, columnWidth }
  }

  calculateNewDefaultColumnCount() {
    const { columns = [] } = this.props
    let defaultColumn = columns.findIndex((_breakpoint) => parseInt(_breakpoint, 10) >= 0)

    if (columns.length <= 0 || defaultColumn === -1) return this.calculateDefaultColumnCount()
    // find default column: breakpoint over 0px.
    defaultColumn = defaultColumn > -1 ? defaultColumn + 1 : DEFAULT_COLUMN_COUNT
    let columnCount =
      defaultColumn > -1 && defaultColumn + 1 >= DEFAULT_COLUMN_COUNT
        ? defaultColumn
        : DEFAULT_COLUMN_COUNT
    // count new column
    columns.forEach((_breakpoint, _columnCount) => {
      if (_breakpoint && this._width >= _breakpoint) {
        columnCount = this._width >= _breakpoint ? _columnCount + 1 : columnCount
      }
    })
    // count new bodyWidth
    const bodyWidth = columnCount > defaultColumn ? this._width : this._width - GUTTER_SIZE + 1
    // count new baseWidth
    const baseWidth =
      bodyWidth - (columnCount > defaultColumn ? columnCount - 1 : columnCount) * this._spacer
    // count new columnWidth
    const columnWidth = Math.floor(baseWidth / columnCount)
    // return new setting
    return { bodyWidth, columnCount, columnWidth }
  }

  _calculateColumnCount() {
    const result = this.calculateNewDefaultColumnCount()
    this._width = result.bodyWidth || this._width
    this._columnCount = result.columnCount || this._columnCount
    this._columnWidth = result.columnWidth || this._columnWidth
    this._spacer = result.spacer || GUTTER_SIZE
  }

  _initCellPositioner = () => {
    if (typeof this._cellPositioner === 'undefined') {
      this._cellPositioner = createMasonryCellPositioner({
        cellMeasurerCache: this._cache,
        columnCount: this._columnCount,
        columnWidth: this._columnWidth,
        spacer: this._spacer,
      })
    }
  }

  _resetCellPositioner = () => {
    this._cellPositioner.reset({
      columnCount: this._columnCount,
      columnWidth: this._columnWidth,
      spacer: this._spacer,
    })
  }

  _setMasonryLayoutRef = (ref) => {
    this._masonrylayout = this._masonrylayout || ref || null
  }

  _setMasonryRef = (ref) => {
    if (typeof window !== 'undefined') {
      window._masonry = window._masonry || ref || null
    }
    this._masonry = this._masonry || ref || null
  }

  _masonryFocusUpdate = () => {
    this._cache.clearAll()
    this._resetCellPositioner()
    this._masonry && this._masonry.clearCellPositions()
  }

  _masonryFocusRecompute = () => {
    this._cache.clearAll()
    this._resetCellPositioner()
    this._masonry && this._masonry.recomputeCellPositions()
  }

  _onResize = ({ width }) => {
    this._width = width || this._width
    // start Resize
    this._calculateColumnCount()
    this._masonryFocusUpdate()
  }

  _handleCellsRendered = ({ stopIndex = 0 } = {}) => {
    const { cells, hasMore } = this.props
    if (hasMore && stopIndex > cells.size - this._columnCount * 2) {
      if (this._handleInfiniteScroll) this._handleInfiniteScroll()
    }
  }

  _cellRenderer = (renderObj) => {
    const { cells, handleRenderCell } = this.props
    const { index, key, parent, style } = renderObj

    const { width, height, ...otherStyle } = style
    const newStyle = { width, height, ...otherStyle }
    if (this.fixedWidth && width) newStyle.width = this._columnWidth
    if (this.fixedHeight && height)
      newStyle.height = Math.floor((style.height / style.width) * this._columnWidth)
    if (this._columnCount === index && cells.getIn([0, 'type']) === 'hidden') newStyle.top = '0'

    return (
      <CellMeasurer cache={this._cache} index={index} key={key} parent={parent}>
        <div key={`masonry-cell-wrap-${key}`} className="masonry-cell-wrap" style={newStyle}>
          {handleRenderCell(index)}
        </div>
      </CellMeasurer>
    )
  }

  _renderMasonry = ({ width }) => {
    const { cells, fixed } = this.props
    this._width = width || this._width

    if (this._masonrylayout) {
      this._calculateColumnCount()
      this._initCellPositioner()
    }

    return this._cellPositioner ? (
      <Masonry
        ref={this._setMasonryRef}
        fixed={fixed}
        autoHeight={true}
        key="masonry-center"
        className="masonry-center"
        cellCount={cells.size}
        cellMeasurerCache={this._cache}
        cellPositioner={this._cellPositioner}
        cellRenderer={this._cellRenderer}
        width={this._width}
        height={this._height}
        overscanByPixels={1000}
        scrollTop={this._scrollTop}
        onCellsRendered={this._handleCellsRendered}
      />
    ) : null
  }

  _renderAutoSizer = ({ height, scrollTop }) => {
    this._height = height
    this._scrollTop = scrollTop

    const { router, hasMore = true, t } = this.props
    const {
      query: { website = '', postID = '' },
    } = router
    const isWebsite = website && !postID

    return (
      <Fragment>
        <style jsx>{styles}</style>
        <div ref={this._setMasonryLayoutRef} className={cx('masonry-layout', { isWebsite })}>
          <AutoSizer
            disableHeight
            height={height}
            cellsSize={this.state.cellsSize}
            onResize={this._onResize}
            overscanByPixels={0}
            scrollTop={this._scrollTop}
            className="masonry-autosizer"
          >
            {this._renderMasonry}
          </AutoSizer>
        </div>
        {hasMore ? <Loading /> : <div className="loadStop">{t('common:到底囉')}</div>}
      </Fragment>
    )
  }

  render() {
    return (
      <WindowScroller
        overscanByPixels={0}
        cellsSize={this.state.cellsSize}
        scrollElement={this.props.scrollElement}
      >
        {this._renderAutoSizer}
      </WindowScroller>
    )
  }
}

export default compose(withRouter, withTranslation(['common']))(MasonryVirtualized)
